/**
 * Client mirror of the program's `koth_end_round` payout math
 * (program/programs/last_touch/src/instructions/koth/end_round.rs).
 *
 * Position-weighted distribution: for `top_n_payout` positions the weights are
 * top_n, top_n-1, …, 1 (position 0 = final king gets the most). Each share is
 * `distributable * weight / sum(weights)`, where
 *   distributable = pot − keeper_bounty − protocol_skim.
 * When fewer than top_n positions are populated, the unfilled positions'
 * weight folds back into the king's share. The last paid position sweeps the
 * integer-division dust so the parts sum exactly to `distributable`.
 *
 * Kept as a pure function (BigInt internally, exact) so it can mirror the
 * on-chain result lamport-for-lamport. If end_round.rs changes, change this.
 */
import { PublicKey } from "@solana/web3.js";

import type { KothRoundState } from "./useKothRound";

const BPS_DENOMINATOR = 10_000n;

export interface WinnerShare {
  /** Ring-buffer position; 0 = final king. */
  position: number;
  /** Winner wallet, base58. */
  wallet: string;
  /** Payout in lamports. */
  lamports: number;
}

export interface WinnersBreakdown {
  shares: WinnerShare[];
  potLamports: number;
  bountyLamports: number;
  skimLamports: number;
  distributableLamports: number;
  topN: number;
}

/**
 * Compute the payout breakdown for a settled round. `potLamports` must be the
 * PRE-settlement pot — end_round zeroes `round.pot_lamports`, so the live PDA
 * reads 0 after settlement and the caller has to supply the captured value.
 * Returns null when the round predates params/ring-buffer (can't be computed).
 */
export function computeWinnersBreakdown(
  round: KothRoundState,
  potLamports: number,
): WinnersBreakdown | null {
  const params = round.paramsSnapshot;
  const touchers = round.recentTouchers;
  if (!params || !touchers) return null;
  const topN = params.topNPayout;
  if (topN < 3) return null;

  const pot = BigInt(Math.max(0, Math.floor(potLamports)));
  const bountyReq = BigInt(params.keeperBountyLamports.toString());
  const bounty = bountyReq < pot ? bountyReq : pot;
  const afterBounty = pot - bounty;
  const skim = (afterBounty * BigInt(params.protocolSkimBps)) / BPS_DENOMINATOR;
  const distributable = afterBounty - skim;

  const populated = touchers
    .filter((p) => !p.equals(PublicKey.default))
    .slice(0, topN);
  const count = populated.length;

  const base = {
    potLamports: Number(pot),
    bountyLamports: Number(bounty),
    skimLamports: Number(skim),
    distributableLamports: Number(distributable),
    topN,
  };
  if (count === 0) return { ...base, shares: [] };

  const totalWeight = BigInt((topN * (topN + 1)) / 2);
  let unallocated = 0n;
  for (let i = count; i < topN; i++) unallocated += BigInt(topN - i);

  const shares: WinnerShare[] = [];
  let allocated = 0n;
  for (let i = 0; i < count; i++) {
    const weight = BigInt(topN - i) + (i === 0 ? unallocated : 0n);
    let share = (distributable * weight) / totalWeight;
    // Last paid position sweeps rounding dust, exactly like the program.
    if (i + 1 === count) share = distributable - allocated;
    allocated += share;
    shares.push({
      position: i,
      wallet: populated[i]!.toBase58(),
      lamports: Number(share),
    });
  }

  return { ...base, shares };
}
